import { useEffect, useState } from 'react'
import BaseWidgetProvider from '../base/BaseWidgetProvider'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import { apiGet } from '../../../generic/Api_Functions'
import { AppBar, Box, CircularProgress, Dialog, IconButton, Stack, Toolbar, Tooltip, Typography } from '@mui/material'
import CenteredText from '../common/CenteredText'
import { useSmartTranslation } from '../../../generic/hooks/useSmartTranslation'
import useMosInfo from '../common/provider/useMosInfo'
import { TaskInstruction } from '../../../configuration/workflow2.0/TaskInstruction'
import { useSmartTextArea } from '../../../generic/smarttextarea/SmartTextArea'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import CloseIcon from '@mui/icons-material/Close'

const TaskInstructionFullscreenDialog = ({ open, onClose, task }) => {
  const { t_ } = useSmartTranslation()
  const { editor } = useSmartTextArea(open ? task?.instructions : undefined)
  const notesEditor = useSmartTextArea(open ? task?.notes : undefined)

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <AppBar position='static' color='default' elevation={0}>
          <Toolbar sx={{ minHeight: 56 }}>
            <Typography variant='h6' sx={{ flexGrow: 1 }}>
              {t_('Task instructions')}
            </Typography>
            <IconButton edge='end' color='inherit' onClick={onClose} aria-label={t_('Close')}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ flexGrow: 1, minHeight: 0, p: 2 }}>
          <TaskInstruction
            editor={editor}
            notesEditor={notesEditor.editor}
            task={task}
            editing={false}
            showDocumentsHeader={false}
          />
        </Box>
      </Box>
    </Dialog>
  )
}

const TaskInstructionContent = ({ task, showDocumentPreviews }) => {
  const { t_ } = useSmartTranslation()
  const [fullscreenOpen, setFullscreenOpen] = useState(false)
  const { editor } = useSmartTextArea(task?.instructions)
  const notesEditor = useSmartTextArea(task?.notes)

  return (
    <Box sx={{ position: 'relative', height: '100%' }}>
      <Stack direction='row' sx={{ position: 'absolute', top: 4, right: 4, zIndex: 2 }}>
        <Tooltip title={t_('Open in full screen')}>
          <span>
            <IconButton
              size='small'
              onClick={() => setFullscreenOpen(true)}
              aria-label={t_('Open in full screen')}
            >
              <OpenInFullIcon fontSize='small' />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <TaskInstructionFullscreenDialog
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        task={task}
      />

      <TaskInstruction
        editor={editor}
        notesEditor={notesEditor.editor}
        task={task}
        editing={false}
        showDocumentsHeader={showDocumentPreviews}
      />
    </Box>
  )
}

const TaskInstructionWidget = (props) => {
  const { preview, data } = props
  const { t_ } = useSmartTranslation()

  const { taskReference } = useMosInfo()
  const [task, setTask] = useState(null)

  // Default to hiding previews (previous behavior); only show them when explicitly enabled.
  const showDocumentPreviews = data?.showDocumentPreviews === true

  useEffect(() => {
    const loadTask = async () => {
      setTask(null)
      if (preview) {
        return
      }
      if (!taskReference) {
        return
      }
      setTask(await apiGet('mos/taskReference/byId/' + taskReference.id))
    }
    loadTask()
  }, [taskReference, preview])

  if (preview) {
    return <Typography>{t_('Instructions')}</Typography>
  }

  if (!taskReference) {
    return <CenteredText><Typography>{t_('No task selected')}</Typography></CenteredText>
  }

  if (task === null) {
    return <CenteredText><CircularProgress /></CenteredText>
  }

  return <TaskInstructionContent key={task.id} task={task} showDocumentPreviews={showDocumentPreviews} />
}

class TaskInstructionWidgetProvider extends BaseWidgetProvider {
  getCode = () => 'TaskInstructions'

  getName = () => ('Task instructions')

  getIcon = () => FormatListNumberedIcon

  createWidget = (props) => <TaskInstructionWidget {...props} />

  createPreviewWidget = (props) => <TaskInstructionWidget {...props} preview />

  size = { defaultW: 6, defaultH: 5, minW: 6, minH: 5 }

  getWidgetInfo = (columns, cellHeight) => {
    return { ...super.getWidgetInfo(columns, cellHeight, this.size) }
  }

  getConfigurationInfo = () => {
    return [
      {
        key: 'showDocumentPreviews',
        type: 'boolean',
        label: 'Show document previews',
        defaultValue: false
      }
    ]
  }
}

export default TaskInstructionWidgetProvider
